import {
  DEFAULT_NYL_IDLE_TIMEOUT_MINUTES,
  isWizardEmail,
  NYL_IDLE_WARNING_MINUTES,
  resolveNylIdleTimeoutPolicy,
  resolveNylIdleTimeoutMinutes,
  SessionType,
} from '@shared';

export { NYL_IDLE_WARNING_MINUTES, resolveNylIdleTimeoutMinutes, resolveNylIdleTimeoutPolicy };
export const IDLE_SESSION_ENDED_PATH = '/session-ended';
const IDLE_SIGN_IN_EMAIL_STORAGE_KEY = 'waterlily:idle-sign-in-email';
const IDLE_SIGN_IN_REQUEST_STORAGE_KEY = 'waterlily:idle-sign-in-request';
const IDLE_SIGN_OUT_TIMEOUT_STORAGE_KEY = 'waterlily:idle-sign-out-timeout';
const IDLE_TIMEOUT_POLICY_STORAGE_PREFIX = 'waterlily:idle-timeout-policy:';
const IDLE_PAGE_LOAD_STORAGE_PREFIX = 'waterlily:idle-page-load:';
const IDLE_SIGN_OUT_REASON_MAX_AGE_MS = 5 * 60 * 1000;

export interface CachedNylIdleTimeoutPolicy {
  isEnabled: boolean;
  timeoutMinutes: number;
}

export interface IdleSignOutReason {
  activityScopeId: string;
  timeoutMinutes: number;
}

type AdvisorSession = Pick<Session, 'sessionType' | 'advisor'>;

export function isNylIdleLogoutSession(
  session: AdvisorSession,
  runtime: 'standard' | 'local-wizard-testing' = 'standard',
): boolean {
  const isEligibleAdvisorSession =
    session.sessionType === SessionType.advisor ||
    (session.sessionType === SessionType.wizard &&
      session.advisor?.featureFlags?.wizardMode === true);
  const isAllowedInternalWizardSession =
    runtime === 'local-wizard-testing' &&
    session.sessionType === SessionType.wizard &&
    session.advisor?.featureFlags?.wizardMode === true;

  return (
    isEligibleAdvisorSession &&
    (!isWizardEmail(session.advisor?.advisorEmail) || isAllowedInternalWizardSession) &&
    session.advisor?.featureFlags?.nylPilotAdvisor === true
  );
}

function minutesToMilliseconds(minutes: number): number {
  const milliseconds = minutes * 60 * 1000;
  return Number.isFinite(milliseconds)
    ? Math.min(milliseconds, Number.MAX_SAFE_INTEGER)
    : Number.MAX_SAFE_INTEGER;
}

export function getNylIdleTimeoutMs(timeoutMinutes: number): number {
  return minutesToMilliseconds(timeoutMinutes);
}

export function getNylIdleWarningMs(timeoutMinutes: number): number | null {
  if (timeoutMinutes <= NYL_IDLE_WARNING_MINUTES) return null;

  return minutesToMilliseconds(timeoutMinutes - NYL_IDLE_WARNING_MINUTES);
}

export function getIdleSessionEndedPath(timeoutMinutes: number): string {
  const searchParams = new URLSearchParams({ timeout: String(timeoutMinutes) });
  return `${IDLE_SESSION_ENDED_PATH}?${searchParams.toString()}`;
}

export function isIdleSessionEndedPath(value: string | null): value is string {
  return (
    value === IDLE_SESSION_ENDED_PATH ||
    value?.startsWith(`${IDLE_SESSION_ENDED_PATH}?`) === true ||
    value?.startsWith(`${IDLE_SESSION_ENDED_PATH}#`) === true
  );
}

export function getIdleSignOutFallbackPath(timeoutMinutes: number): string {
  const searchParams = new URLSearchParams({ reason: 'idle', timeout: String(timeoutMinutes) });
  return `/auth/signout?${searchParams.toString()}`;
}

export function storeIdleSignInEmail(email?: string | null): void {
  try {
    if (email) sessionStorage.setItem(IDLE_SIGN_IN_EMAIL_STORAGE_KEY, email);
    else sessionStorage.removeItem(IDLE_SIGN_IN_EMAIL_STORAGE_KEY);
  } catch {
    // Email prefill is best effort and must never prevent sign-out.
  }
}

export function readIdleSignInEmail(): string | null {
  try {
    return sessionStorage.getItem(IDLE_SIGN_IN_EMAIL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearIdleSignInEmail(): void {
  try {
    sessionStorage.removeItem(IDLE_SIGN_IN_EMAIL_STORAGE_KEY);
  } catch {
    // Email prefill cleanup is best effort.
  }
}

export function storeIdleSignInRequest(): void {
  try {
    sessionStorage.setItem(IDLE_SIGN_IN_REQUEST_STORAGE_KEY, 'true');
  } catch {
    // In-memory page state still covers sign-out flows that do not reload the page.
  }
}

export function readIdleSignInRequest(): boolean {
  try {
    return sessionStorage.getItem(IDLE_SIGN_IN_REQUEST_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function clearIdleSignInRequest(): void {
  try {
    sessionStorage.removeItem(IDLE_SIGN_IN_REQUEST_STORAGE_KEY);
  } catch {
    // Sign-in request cleanup is best effort.
  }
}

export function storeIdleSignOutTimeout(
  activityScopeId: string,
  timeoutMinutes: number,
  storedAt = Date.now(),
): void {
  try {
    localStorage.setItem(
      IDLE_SIGN_OUT_TIMEOUT_STORAGE_KEY,
      JSON.stringify({ activityScopeId, timeoutMinutes, storedAt }),
    );
  } catch {
    // Missing reason state safely falls back to the ordinary sign-out flow.
  }
}

export function readIdleSignOutReason(now = Date.now()): IdleSignOutReason | null {
  try {
    const storedValue = localStorage.getItem(IDLE_SIGN_OUT_TIMEOUT_STORAGE_KEY);
    if (storedValue === null) return null;

    const storedReason = JSON.parse(storedValue) as {
      activityScopeId?: unknown;
      timeoutMinutes?: unknown;
      storedAt?: unknown;
    };
    const hasValidActivityScopeId =
      typeof storedReason.activityScopeId === 'string' && storedReason.activityScopeId.length > 0;
    const hasValidTimeout =
      typeof storedReason.timeoutMinutes === 'number' &&
      Number.isFinite(storedReason.timeoutMinutes) &&
      Number.isInteger(storedReason.timeoutMinutes) &&
      storedReason.timeoutMinutes >= 1;
    const hasFreshReason =
      typeof storedReason.storedAt === 'number' &&
      Number.isFinite(storedReason.storedAt) &&
      storedReason.storedAt <= now &&
      now - storedReason.storedAt <= IDLE_SIGN_OUT_REASON_MAX_AGE_MS;

    return hasValidActivityScopeId && hasValidTimeout && hasFreshReason
      ? {
          activityScopeId: storedReason.activityScopeId as string,
          timeoutMinutes: storedReason.timeoutMinutes as number,
        }
      : null;
  } catch {
    return null;
  }
}

export function readIdleSignOutTimeout(activityScopeId: string, now = Date.now()): number | null {
  const storedReason = readIdleSignOutReason(now);
  return storedReason?.activityScopeId === activityScopeId ? storedReason.timeoutMinutes : null;
}

export function clearIdleSignOutTimeout(): void {
  try {
    localStorage.removeItem(IDLE_SIGN_OUT_TIMEOUT_STORAGE_KEY);
  } catch {
    // Timeout cleanup is best effort.
  }
}

export function formatIdleWarningCountdown(remainingSeconds: number): string {
  const safeSeconds = Number.isFinite(remainingSeconds)
    ? Math.max(0, Math.floor(remainingSeconds))
    : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = String(safeSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

export function parseIdleTimeoutMinutes(value: string | null): number {
  if (value === null || value.trim() === '') {
    return DEFAULT_NYL_IDLE_TIMEOUT_MINUTES;
  }

  return resolveNylIdleTimeoutMinutes({ nylIdleTimeoutMinutes: Number(value) });
}

export function getIdleActivityStorageKey(activityScopeId: string): string {
  return `waterlily:idle-activity:${activityScopeId}`;
}

export function getIdleTimeoutPolicyStorageKey(activityScopeId: string): string {
  return `${IDLE_TIMEOUT_POLICY_STORAGE_PREFIX}${activityScopeId}`;
}

export function getIdlePageLoadStorageKey(activityScopeId: string): string {
  return `${IDLE_PAGE_LOAD_STORAGE_PREFIX}${activityScopeId}`;
}

/**
 * Tells an already-open tab that the same cached NYL user is loading Waterlily elsewhere. This is
 * separate from activity storage so a receiving tab can first verify that the existing deadline
 * has not expired, then count this Waterlily page load as shared activity.
 */
export function announceCachedNylIdlePageLoad(
  activityScopeId: string,
  occurredAt = Date.now(),
): boolean {
  if (readCachedNylIdleTimeoutPolicy(activityScopeId)?.isEnabled !== true) return false;

  try {
    const storageKey = getIdlePageLoadStorageKey(activityScopeId);
    localStorage.setItem(storageKey, String(occurredAt));
    localStorage.removeItem(storageKey);
    return true;
  } catch {
    return false;
  }
}

export function readCachedNylIdleTimeoutPolicy(
  activityScopeId: string,
): CachedNylIdleTimeoutPolicy | null {
  try {
    const storedPolicy = localStorage.getItem(getIdleTimeoutPolicyStorageKey(activityScopeId));
    if (storedPolicy === null) return null;

    const timeoutMinutes = Number(storedPolicy);
    if (
      !Number.isFinite(timeoutMinutes) ||
      !Number.isInteger(timeoutMinutes) ||
      timeoutMinutes < 1
    ) {
      return null;
    }

    return { isEnabled: true, timeoutMinutes };
  } catch {
    return null;
  }
}

export function storeCachedNylIdleTimeoutPolicy(
  activityScopeId: string,
  policy: CachedNylIdleTimeoutPolicy,
): void {
  try {
    const storageKey = getIdleTimeoutPolicyStorageKey(activityScopeId);
    if (!policy.isEnabled) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(storageKey, String(policy.timeoutMinutes));
  } catch {
    // The current AppConfig value still controls the session when browser storage is unavailable.
  }
}

export function clearCachedNylIdleTimeoutPolicies(): void {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(IDLE_TIMEOUT_POLICY_STORAGE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  } catch {
    // Timeout cache cleanup is best effort.
  }
}

export function readIdleActivityAt(activityScopeId: string, now: number): number | null {
  try {
    const storedAt = Number(localStorage.getItem(getIdleActivityStorageKey(activityScopeId)));
    return Number.isFinite(storedAt) && storedAt > 0 && storedAt <= now ? storedAt : null;
  } catch {
    return null;
  }
}

export function isIdleActivityExpired(
  activityScopeId: string,
  timeoutMs: number,
  now = Date.now(),
): boolean {
  const activityAt = readIdleActivityAt(activityScopeId, now);
  return activityAt !== null && now - activityAt >= timeoutMs;
}
